import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Picture } from "@/components/wpl/image/src";
import { Link } from "@/libraries/design/components";
import { Row } from "@/libraries/wpl/foundations/src";

const pageSection = css({
    marginInlineEnd: "auto",
    marginInlineStart: "auto",
    minWidth: "100%",
    padding: `${token("space.400")} ${token("space.0")}`,
    clear: "both",
    backgroundColor: "#ffffff",
    position: "relative"
});

const sectionCss = css({
    padding: "32px 0",
    position: "relative",
    verticalAlign: "baseline",
    marginInlineEnd: "auto",
    marginInlineStart: "auto",
    minWidth: "100%"
});
const headerStyle = css({
    textAlign: "center",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    h2: {
        color: "#253858",
        fontWeight: "500",
        padding: "16px 0",
        lineHeight: " 1.33333333",

        [media.below.sm]: {
            margin: "0 0 14px",
            fontSize: token("space.300")
        },

        [media.only.sm]: {
            margin: "0",
            fontSize: "2.25rem"
        },

        [media.above.md]: {
            margin: "0",
            fontSize: "2.25rem"
        }
    }
});

const getTheMostCard = css({
    "borderRadius": "2px",
    "boxShadow": `1px 1px 6px ${"rgba(0, 0, 0, .1)"}`,
    "display": "flex",
    "maxWidth": "1100px",
    "containerType": "inline-size",
    "backgroundColor": "#ffffff",
    "color": "#091e42",
    "flex": "4",
    "padding": "2.5rem",
    "margin": "0 auto",
    "width": "100%",

    [media.above.md]: {
        display: "block"
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".row-layout": {
        [media.below.xs]: {
            gap: "16px"
        },
        [media.above.xs]: {
            gridTemplateColumns: "minmax(0px, .25fr) minmax(0px, .75fr)"
        },
        [media.above.sm]: {
            gap: "16px"
        }
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".img-section": {
        "display": "flex",
        "alignItems": "baseline",
        "justifyContent": "center",
        "paddingBlockStart": "16px",
        "paddingBlockEnd": 0,

        // [media.only.sm]: {
        //     paddingBlockStart: 0
        // },

        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".atlassianBookIllustration": {
            width: "20%",

            [media.only.xs]: {
                width: "52%"
            },
            [media.only.sm]: {
                width: "70%"
            },
            [media.only.md]: {
                width: "100%"
            },
            [media.above.md]: {
                width: "80%"
            }
        },

        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".integrationIllustration": {
            width: "20%",
            [media.only.xs]: {
                width: "70%"
            },
            [media.only.sm]: {
                width: "70%"
            },
            [media.only.md]: {
                width: "100%"
            },
            [media.above.md]: {
                width: "80%"
            }
        }
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".link-wrapper span": {
        fontWeight: "400"
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".text-section": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "h3": {
            marginBlockStart: "0.6rem",
            marginBlockEnd: "0",
            fontWeight: "500",
            [media.above.sm]: {
                fontSize: "1.5rem"
            },
            [media.below.sm]: {
                fontSize: "1.25rem"
            },
            lineHeight: "1.2"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "p": {
            fontSize: "1rem"
        },

        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".card-linkFlex": {
            margin: "auto 0 0",
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            a: {
                margin: "0"
            }
        }
    }
});

const GetTheMostSection = () => {
    const intl = useIntl();
    const cardData = [
        {
            pictureProps: {
                image: {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:76e94ebd-d71e-4991-9008-7b2e838090c8/illustrations-spot-Integrations.svg?cdnVersion=1418",
                    alt: intl.formatMessage({
                        id: "TryPageTemplate.getTheMost.integrationIllustration",
                        defaultMessage: "integration illustration"
                    }),
                    className: "integrationIllustration"
                }
            },
            bodyText: intl.formatMessage({
                id: "TryPageTemplate.getTheMost.marketplaceBodyText",
                defaultMessage:
                    "Extend your Atlassian products with over 1,000+ apps and integrations from Marketplace."
            }),
            headerText: intl.formatMessage({
                id: "TryPageTemplate.getTheMost.marketplaceHeaderText",
                defaultMessage: "Tailor to your team’s needs"
            }),
            primaryLink: {
                text: intl.formatMessage({
                    id: "TryPageTemplate.getTheMost.marketplacePrimaryLink",
                    defaultMessage: "Go to Marketplace"
                }),
                dataLabelEnglish: "Go to Marketplace",
                link: "https://marketplace.atlassian.com/"
            }
        },
        {
            pictureProps: {
                image: {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:a88ae3d8-e6fd-4428-8a14-c9ce6b460b0e/illustrations-spot-Atlassian%20Book_1.svg?cdnVersion=1418",
                    alt: intl.formatMessage({
                        id: "TryPageTemplate.getTheMost.atlassianBookIllustration",
                        defaultMessage: "Atlassian book illustration"
                    }),
                    className: "atlassianBookIllustration"
                }
            },
            bodyText: intl.formatMessage({
                id: "TryPageTemplate.getTheMost.universityBodyText",
                defaultMessage:
                    "Develop new skills, improve your team’s work, and advance your professional career."
            }),
            headerText: intl.formatMessage({
                id: "TryPageTemplate.getTheMost.universityHeaderText",
                defaultMessage: "Train with Atlassian University"
            }),
            primaryLink: {
                text: intl.formatMessage({
                    id: "TryPageTemplate.getTheMost.universityPrimaryLink",
                    defaultMessage: "Go to Atlassian University"
                }),
                dataLabelEnglish: "Go to Atlassian University",
                link: "https://www.atlassian.com/university"
            }
        }
    ];
    return (
        <section css={sectionCss}>
            <div css={pageSection}>
                <Row>
                    <div css={headerStyle}>
                        <h2>
                            <FormattedMessage
                                id="TryPageTemplate.getTheMost.header"
                                defaultMessage="Get the most out of your products"
                            />
                        </h2>
                    </div>
                </Row>
                <Row layout="6-6">
                    {cardData.map((card, index) => (
                        <div css={getTheMostCard} key={index}>
                            <Row layout="3-9">
                                <div className={"img-section"}>
                                    <Picture {...card.pictureProps} />
                                </div>
                                <div className={"text-section"}>
                                    <div>
                                        <h3>{card.headerText}</h3>
                                        <p className={"card-body"}>
                                            {card.bodyText}
                                        </p>
                                    </div>
                                    <div className={"card-linkFlex"}>
                                        <Link
                                            type={"primary"}
                                            text={card.primaryLink.text}
                                            href={card.primaryLink.link}
                                            dataEventProps={{
                                                "data-product-key": "try",
                                                "data-event-component":
                                                    "linkButton",
                                                "data-event-container":
                                                    "getTheMostSection",
                                                "data-event": "clicked",
                                                "data-label":
                                                    card.primaryLink.text,
                                                "data-label-english":
                                                    card.primaryLink
                                                        .dataLabelEnglish
                                            }}
                                        />
                                    </div>
                                </div>
                            </Row>
                        </div>
                    ))}
                </Row>
            </div>
        </section>
    );
};

export default GetTheMostSection;
